import React, { useState } from 'react';
import { auth, functions } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';

// Emails that go straight to the Ben portal — no admin claim required, so
// these don't need REACT_APP_ADMIN_EMAILS to be set.
const BEN_PORTAL_EMAILS = ['ben@lended.ai', 'isak@lended.ai'];

function AdminSignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  // Guard against the env var being unset (was crashing on `.split` when undefined).
  const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);

  const handleAdminSignUp = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const isBenPortal = BEN_PORTAL_EMAILS.includes(normalizedEmail);
    const isMainAdmin = adminEmails.includes(normalizedEmail);

    if (!isBenPortal && !isMainAdmin) {
      setError('Access denied. Email is not authorized for admin access.');
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const user = userCredential.user;
      console.log('Account created:', user.email);

      if (isBenPortal) {
        // Ben/Isak don't need the admin claim — /ben-portal gates by email.
        navigate('/ben-portal');
        return;
      }

      // Main admins still get the custom claim via the Cloud Function.
      try {
        const setAdminClaim = httpsCallable(functions, 'setAdminClaim');
        await setAdminClaim({ uid: user.uid });
      } catch (claimErr) {
        console.warn('setAdminClaim failed (continuing anyway):', claimErr);
      }
      navigate('/admindashboard');
    } catch (err) {
      console.log('Error signing up:', err);
      setError(err?.message || 'Error signing up. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-blue-900 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-sm space-y-10 bg-white p-8 rounded-lg shadow-lg">
        <div>
          <img
            className="mx-auto h-12 w-auto"
            src="https://tailwindui.com/img/logos/mark.svg?color=blue&shade=600"
            alt="Your Company"
          />
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Admin Sign Up
          </h2>
        </div>
        <form className="space-y-6" onSubmit={handleAdminSignUp}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Sign Up
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminSignUp;